import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "../../RootReducer";
import { getMethods } from "../../selectors/payments-selectors";
import { getActiveCurrency } from "../../selectors/user-selectors";
import { selectMethod } from "../../payments/PaymentsActions";
import { t } from "../../i18n";
import CryptoDeposit from "../../ui/paymentproviders/sharedcomponents/CryptoDeposit";
import "./CryptoMethods.scss";
import { initDeposit, resetDepositStatusAndContext } from "../../payments/DepositActions";
import Loader from "../../layout/Loader";
import { CryptoSelects } from "./CryptoSelects";
import { ContextStatus, PaymentStateStatus } from "../../payments/PaymentsReducer";
import ChooseAmount from "../../ui/deposit/ChooseAmount";

interface CryptoDepositMethodsProps {
  groupName?: string;
  isKycRequirementsNotMet?: boolean;
}

const CryptoDepositMethods: React.FC<CryptoDepositMethodsProps> = ({ groupName, isKycRequirementsNotMet }) => {
  const dispatch = useDispatch();
  const [showAmountInput, setShowAmountInput] = useState(false);
  const methods = useSelector((state: AppState) => getMethods(state));
  const groupMethods = methods?.filter((m) => m.group === groupName) ?? [];
  const activeSiteCurrency = useSelector((state: AppState) => getActiveCurrency(state));
  const deposit = useSelector((state: AppState) => state.payments.deposit);
  const [loadingNetwork, setLoadingNetwork] = useState(false);
  const activeMethod = deposit.activeMethod;
  const depositDialog = deposit.depositDialog;
  const depositStatus = deposit.status;
  const contextStatus = deposit.depositContextStatus;

  const onConfirmAmount = () => {
    setShowAmountInput(false);
    setLoadingNetwork(false);
    dispatch(initDeposit() as any);
  };

  const onNetworkChange = async (networkCode: string | undefined, selectedMethodCode: string | undefined) => {
    if (!networkCode || !activeSiteCurrency?.code) return;

    setLoadingNetwork(true);
    const selectedMethodCopy = { ...groupMethods.find((m) => m.code === selectedMethodCode) };
    const selectedNetwork = selectedMethodCopy.networks?.find((n) => n.code === networkCode);
    selectedMethodCopy.code = networkCode;

    await dispatch(selectMethod(selectedMethodCopy, activeSiteCurrency?.code) as any);

    if (selectedNetwork?.amountRequired) {
      setShowAmountInput(true);
      setLoadingNetwork(false);
      return;
    }

    setShowAmountInput(false);
    await dispatch(initDeposit() as any);
    setLoadingNetwork(false);
  };

  const onMethodChange = async (methodCode: string | undefined) => {
    if (!methodCode || !activeSiteCurrency?.code) return;

    dispatch(resetDepositStatusAndContext());
  };

  return (
    <>
      <div className="group-specific-methods" role="region">
        <div className="method-selector">
          <CryptoSelects
            groupMethods={groupMethods}
            onMethodChange={onMethodChange}
            onNetworkChange={onNetworkChange}
            initialValue={activeMethod?.code}
            latestUsedMethodCode={groupMethods.find((m) => m.isLatest || m.isLatestCryptoDeposit)?.code}
          />
        </div>

        {!isKycRequirementsNotMet && contextStatus !== ContextStatus.ERROR && (
          <>
            {showAmountInput && (
              <div className="crypto-amount-input">
                <ChooseAmount handleOnClick={() => onConfirmAmount()} />
              </div>
            )}
            {!showAmountInput && depositDialog?.customContext?.address ? (
              <CryptoDeposit
                address={depositDialog.customContext.address}
                code={activeMethod?.code}
                customContext={depositDialog.customContext}
                currencyCode={activeSiteCurrency?.code || ""}
                decimals={activeSiteCurrency?.fractionalDigits || 0}
                warningText={t("payments.maxDecimalsWarning")}
                generateNew={activeMethod?.canReuseAddress}
              />
            ) : (
              loadingNetwork && (
                <div className="crypto-group-methods-loader-container">
                  <Loader />
                </div>
              )
            )}
          </>
        )}
      </div>
      {!isKycRequirementsNotMet && contextStatus === ContextStatus.ERROR && (
        <div className="contextError">
          <p className="text-danger" style={{ textAlign: "center" }}>
            {t("deposit.context-error")}
          </p>
        </div>
      )}
      {!isKycRequirementsNotMet && depositStatus === PaymentStateStatus.DEPOSIT_ERROR_INITIALIZING && (
        <div className={"deposit-content"}>
          <p className="text-danger" style={{ textAlign: "center" }}>
            {t("deposit.context-error")}
          </p>
        </div>
      )}
    </>
  );
};

export default CryptoDepositMethods;
